import { toast } from "sonner";

/**
 * Web Share nativo con fallback de portapapeles.
 *
 *   1. Si `navigator.share` existe → llamar al share nativo con el payload.
 *   2. Si no, o si el share nativo falla con un error ≠ AbortError → copiar la
 *      URL al portapapeles (Clipboard API + fallback a execCommand) y mostrar
 *      un toast con el texto EXACTO "¡Enlace copiado al portapapeles!".
 *   3. Si el usuario cancela (AbortError) → nada, sin toast.
 *
 * Devuelve `{ mode }` (para tests): "native" | "clipboard" | "cancelled".
 */
export async function shareOrCopy({ title, text, url, ref }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const base = url || origin;
  // Append a viral ?ref=<ondatag> to enable referral tracking on landing.
  // The tag is normalized (no leading "@", lowercase) upstream by the caller;
  // we defensively re-normalize here.
  let targetUrl = base;
  if (ref && typeof ref === "string") {
    const normRef = ref.replace(/^@/, "").trim().toLowerCase();
    if (normRef) {
      try {
        const u = new URL(base);
        u.searchParams.set("ref", normRef);
        targetUrl = u.toString();
      } catch (_e) {
        const sep = base.includes("?") ? "&" : "?";
        targetUrl = `${base}${sep}ref=${encodeURIComponent(normRef)}`;
      }
    }
  }
  const payload = { title, text, url: targetUrl };

  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      await navigator.share(payload);
      return { mode: "native" };
    } catch (err) {
      const name = err && (err.name || "");
      if (name === "AbortError") return { mode: "cancelled" };
      // fall through to clipboard fallback for any other error
    }
  }

  const copied = await copyToClipboard(targetUrl);
  if (copied) {
    toast.success("¡Enlace copiado! Gana 5 NexaTokens por cada amigo que se registre.");
    try {
      // Global event → NexaTokensChip listens & runs a one-shot pulse animation
      // to reinforce the reward moment (viral loop).
      window.dispatchEvent(new CustomEvent("queonda.tokens.pulse", { detail: { reason: "share" } }));
    } catch (_e) {
      /* ignore */
    }
    return { mode: "clipboard" };
  }
  toast.error("No pude compartir. Copia el enlace manualmente: " + targetUrl);
  return { mode: "failed" };
}

async function copyToClipboard(text) {
  if (!text) return false;
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_e) {
    /* try legacy fallback */
  }
  // Legacy fallback via a temporary textarea + execCommand
  if (typeof document === "undefined") return false;
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "absolute";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return !!ok;
  } catch (_e) {
    return false;
  }
}
